import { useParams, useNavigate } from 'react-router-dom';
import { categoryData } from '@/mocks/categories';

const guideContents: Record<string, { title: string; subtitle: string; steps: { num: string; title: string; desc: string }[] }> = {
  dress: {
    title: '예복, 무엇부터 보면 좋을까요?',
    subtitle: '몇 가지 질문에 답하면 예산, 취향, 방문 기준이 자연스럽게 정리돼요.',
    steps: [
      { num: '01', title: '기준 이해', desc: '제작 방식, 원단, 핏, 수선 등 예복의 핵심 기준을 알아보세요' },
      { num: '02', title: '취향 선택', desc: '원하는 분위기와 중요한 기준을 선택하세요' },
      { num: '03', title: '결과 확인', desc: '나에게 맞는 예복 준비 기준과 추천 업체를 확인하세요' },
    ],
  },
  jewelry: {
    title: '예물, 무엇부터 정하면 좋을까요?',
    subtitle: '커플링과 다이아 반지의 기준, 브랜드와 공방의 차이를 알아보세요.',
    steps: [
      { num: '01', title: '기준 이해', desc: '브랜드/공방 차이, A/S, 보증서, 예산 범위를 알아보세요' },
      { num: '02', title: '취향 선택', desc: '디자인 취향과 가장 중요한 기준을 선택하세요' },
      { num: '03', title: '결과 확인', desc: '나에게 맞는 예물 준비 기준과 추천 업체를 확인하세요' },
    ],
  },
  hanbok: {
    title: '한복, 누구부터 입을지 정해볼까요?',
    subtitle: '신랑신부와 혼주의 구성, 맞춤과 대여의 차이를 알아보세요.',
    steps: [
      { num: '01', title: '기준 이해', desc: '착용 대상, 맞춤/대여, 촬영/본식, 피팅 일정을 알아보세요' },
      { num: '02', title: '취향 선택', desc: '원하는 색감과 분위기, 중요한 기준을 선택하세요' },
      { num: '03', title: '결과 확인', desc: '나에게 맞는 한복 준비 기준과 추천 업체를 확인하세요' },
    ],
  },
};

const infoCards: Record<string, { title: string; points: string[] }> = {
  dress: {
    title: '예복에서 교육할 기준',
    points: ['제작 방식: 대여 / 맞춤 / 반수제 / 수제', '본식 예복 퀄리티', '촬영용 대여복 구성', '원단과 핏', '수선과 구성품 포함 여부', '가격대와 상담 만족도'],
  },
  jewelry: {
    title: '예물에서 교육할 기준',
    points: ['커플링 중심인지 다이아 포함 여부', '브랜드와 공방의 차이', '예산 범위와 디자인 취향', 'A/S와 보증서', '상담 투명성'],
  },
  hanbok: {
    title: '한복에서 교육할 기준',
    points: ['누가 입는지: 신랑신부 / 혼주', '맞춤과 대여의 차이', '촬영용과 본식용 구분', '색감과 원단', '피팅 일정과 반납 조건'],
  },
};

export default function Guide() {
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();

  const cat = category as keyof typeof categoryData;
  const catInfo = categoryData[cat];
  const guide = guideContents[cat];
  const info = infoCards[cat];

  if (!guide || !catInfo) {
    return (
      <div className="min-h-[100dvh] bg-cream flex items-center justify-center">
        <p>존재하지 않는 카테고리입니다</p>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-cream flex flex-col">
      <div className="bg-white px-5 pt-6 pb-4 border-b border-cream-dark">
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => navigate('/home')}
            className="w-8 h-8 flex items-center justify-center text-navy cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <span className="text-beige-dark text-sm">{catInfo.name}</span>
        </div>
        <span
          className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-3"
          style={{ backgroundColor: `${catInfo.color}12`, color: catInfo.color }}
        >
          {catInfo.name} 준비 가이드
        </span>
        <h1 className="font-serif-display text-navy text-2xl font-bold leading-snug">
          {guide.title}
        </h1>
        <p className="text-beige-dark text-sm mt-3 leading-relaxed">{guide.subtitle}</p>
      </div>

      <div className="flex-1 px-5 py-6 overflow-y-auto">
        <p className="text-navy font-semibold text-sm mb-4">진행 방식</p>
        <div className="space-y-3">
          {guide.steps.map((step) => (
            <div
              key={step.num}
              className="bg-white rounded-xl p-4 border border-cream-dark flex items-start gap-4"
            >
              <span className="text-softgold font-bold text-lg shrink-0">{step.num}</span>
              <div>
                <p className="text-navy font-semibold text-sm">{step.title}</p>
                <p className="text-beige-dark text-xs mt-1 leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 bg-white rounded-2xl p-5 border border-cream-dark">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${catInfo.color}12`, color: catInfo.color }}>
              <i className="ri-lightbulb-line text-sm" />
            </div>
            <p className="text-navy font-semibold text-sm">{info.title}</p>
          </div>
          <div className="space-y-2">
            {info.points.map((point, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-softgold mt-0.5 shrink-0">·</span>
                <span className="text-gray-600 text-sm leading-relaxed">{point}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 bg-cream rounded-xl p-4 border border-cream-dark">
          <p className="text-navy text-sm font-medium text-center">
            기준 이해 → 취향 선택 → 결과 확인 순서로 진행돼요
          </p>
        </div>
      </div>

      <div className="px-5 pb-[env(safe-area-inset-bottom)] pt-3 bg-white border-t border-cream-dark">
        <button
          onClick={() => navigate(`/diagnose/${cat}`)}
          className="w-full bg-navy text-white rounded-xl py-4 font-semibold text-base cursor-pointer hover:bg-navy-light transition-colors whitespace-nowrap"
        >
          {catInfo.name} 준비 시작하기
        </button>
        <button
          onClick={() => navigate('/vendors')}
          className="w-full mt-2 bg-white text-navy rounded-xl py-3 font-medium text-sm border border-cream-dark cursor-pointer hover:bg-cream transition-colors whitespace-nowrap"
        >
          업체 바로 보기
        </button>
      </div>
    </div>
  );
}